import { useRequest } from '@byted/hooks';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { Empty, Spin } from '@douyinfe/semi-ui';
import {
  IllustrationNoResult,
  IllustrationNoResultDark,
} from '@douyinfe/semi-illustrations';
import { PostItem } from './PostItem';
import { useScope } from '@/hooks/useScope';
import { Post } from '@/typings/open.platform.forum_bff/namespaces/forumpost';
import { forumPostService } from '@/api/forumPost';
import { PostType } from '@/typings/fe';
import { useUserInfo } from '@/hooks/useUserInfo';
import usePost from '@/context/usePost';
import useBiz from '@/context/useBiz';

type Props = {
  scrollParentRef: React.MutableRefObject<HTMLDivElement | null>;
  postType?: PostType;
  choice: 'recommend' | 'hottest';
} & React.HTMLAttributes<HTMLDivElement>;
export const PostList = ({
  scrollParentRef,
  postType = 'all',
  choice,
}: Props) => {
  const biz = useBiz(s => s.biz);
  const scope = useScope();
  const [postList, setPostList] = useState<Post[]>([]);
  const { isAdmin } = useUserInfo();
  const fetcher = async (pageNo: number) => {
    const res = await forumPostService.getPosts({
      Type: postType,
      Scope: scope,
      Uid: '',
      Biz: biz,
      Choice: choice,
      PageNo: pageNo,
      PageSize: 10,
    });
    if (res.error) {
      return {
        hasMore: false,
        list: [],
        pageNo,
      };
    }
    return {
      hasMore: res.data.List.length === 10,
      list: res.data.List,
      pageNo,
    };
  };
  const { run, loading, data, reset } = useRequest(fetcher, {
    auto: false,
    // Trick 销毁社区子应用后不发起请求
    ready: window.location.pathname.includes('forum') && biz !== 0,
    onSuccess: res => {
      if (res) {
        setPostList(list => [...list, ...res.list]);
      }
    },
  });
  useEffect(() => {
    if (biz === 0) {
      return;
    }
    setPostList([]);
    run(1);
  }, [scope, choice, biz]);

  useEffect(() => {
    const unsubscribe = usePost.subscribe(state => {
      if (state.shouldRefetch) {
        setPostList([]);
        run(1);
        usePost.setState({ shouldRefetch: false });
      }
    });
    return () => {
      unsubscribe();
    };
  }, [reset]);

  if (loading && !postList.length) {
    return (
      <div className=" h-60  w-full flex justify-center items-center">
        <Spin />
      </div>
    );
  }
  if (postList.length === 0) {
    return (
      <Empty
        className="h-[300px] mb-[80px]"
        image={
          <IllustrationNoResult
            style={{ width: 150, height: 150 }}
            className="mt-16"
          />
        }
        darkModeImage={
          <IllustrationNoResultDark
            style={{ width: 150, height: 150 }}
            className="mt-16"
          />
        }
        description="暂无内容，欢迎发帖"
      />
    );
  }
  return (
    <InfiniteScroll
      className="mb-4"
      getScrollParent={() => scrollParentRef.current}
      initialLoad={false}
      pageStart={1}
      threshold={20}
      useWindow={false}
      hasMore={data?.hasMore && !loading}
      loadMore={page => {
        if (!loading && data?.hasMore) {
          run(page);
        }
      }}>
      {postList.map((post, index) => (
        <PostItem post={post} isAdmin={isAdmin} scope={scope} key={index} />
      ))}
      {loading && data?.hasMore && (
        <div style={{ textAlign: 'center' }}>
          <Spin />
        </div>
      )}
    </InfiniteScroll>
  );
};
